/** @license @dairyfarm/eval-web v3.0.0
* Copyright 2020 Texas A&M Center for Applied Technology, 
* Texas A&M Engineering Experiment Station, The Texas A&M 
* University System.
* 
* This source code/schema and any derivative work is Texas 
* A&M Engineering Experiment Station intellectual property 
* and protected by copyright law. Usage is restricted solely 
* to the FARM Animal Care and Environmental Stewardship 
* programs administered by the National Milk Producers 
* Federation. Dissemination of this source code and/or 
* derivative work creation is strictly prohibited without 
* express written permission from the Texas A&M Engineering 
* Experiment Station.
*/
import { Container, createStyles, Divider, List, Paper, TablePagination, Theme, Typography } from "@material-ui/core";
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";
import { makeStyles } from "@material-ui/styles";
import { PDFViewer } from '@react-pdf/renderer';
import React, { FC, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import { AppState, loadEvaluations, syncSystem } from "../../redux";
import { selectEvals, selectUserId } from "../hooks";
import EvalListItem from "./EvalListItem";
import { ErrorBlock } from './form/components/ErrorBlock';
import LoadingIndicator from './form/components/LoadingIndicator';
import NewEvalButton from "./NewEvalButton";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: "100%",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      zIndex: 0,
      // [theme.breakpoints.up('xs')]: {
      //   overflowY: "scroll",
      // },
      WebkitOverflowScrolling: "touch",
    },
    toolbar: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      position: "relative",
      padding: theme.spacing(1, 3),
      [theme.breakpoints.down("xs")]: {
        padding: theme.spacing(1),
      },
      // backgroundColor: theme.palette.grey[200],
      backgroundColor: theme.palette.background.paper,
    },
    buttonGroupContainer: {
      display: "flex",
      flex: 1,
      alignItems: "center",
    },
    modeButton: {
      textTransform: "none",
    },
    modeButtonRoot: {
      padding: theme.spacing(0,3),
      height: "32px",
      "&.Mui-selected": {
        backgroundColor: theme.palette.primary.light,
        color: theme.palette.getContrastText(theme.palette.primary.main),
        "&:hover": {
          backgroundColor: theme.palette.primary.main,
        },
      },
    },
    content: {
      flex: 1,
      marginTop: theme.spacing(3),
      marginBottom: theme.spacing(3),
    },
    noEvalsMessage: {
      margin: theme.spacing(1, 3),
    },
    list: {
      marginBottom: theme.spacing(10),
      display: "flex",
      flexDirection: "column",
    },
    loading: {
      backgroundColor: "#fff",
    },
    pagination: {},
  })
);

const selectDraftMode = createSelector(
  (state: AppState) => state.evals.draftMode,
  draftMode => draftMode
);
const selectError = createSelector(
  (state: AppState) => state.evals.error,
  error => error
);
const selectLoadingEvals = createSelector(
  (state: AppState) => state.evals.loading,
  loading => loading
);
const selectEvalLimit = createSelector(
  (state: AppState) => state.evals.limit,
  limit => limit
);
const selectEvalOffset = createSelector(
  (state: AppState) => state.evals.offset,
  offset => offset
);
const selectEvalCount = createSelector(
  (state: AppState) => state.evals.total,
  count => count
);

interface EvalListProperties {}

const EvalList: FC<EvalListProperties> = props => {
  const classes = useStyles(props);
  const draftMode: boolean|null = useSelector(selectDraftMode);
  const evals = useSelector(selectEvals);
  const error = useSelector(selectError);
  const accountId = useSelector(selectUserId);
  const loadingEvals = useSelector(selectLoadingEvals);

  const totalEvals = useSelector(selectEvalCount);
  const limit = useSelector(selectEvalLimit);
  const offset = useSelector(selectEvalOffset);

  const dispatch = useDispatch();

  const fetchingEvals = useSelector((state: AppState) => state.evals.isFetching);

  useEffect(() => {
    if (!fetchingEvals) {
      dispatch(loadEvaluations(draftMode ?? true, 25));
    }
  }, [fetchingEvals, draftMode, dispatch])

  useEffect(
    function refresh() {
      dispatch(syncSystem());
    },
    [accountId, dispatch, draftMode]
  );

  if (error) {
    return (
      <div className={classes.root}>
        <ErrorBlock name={error.name} error={error} message={error.message} />
      </div>
    );
  }

  const handleListMode = (event: React.MouseEvent<HTMLElement>, newMode: boolean|null) => {
    if (newMode !== null) {
      dispatch(loadEvaluations(newMode, limit));
    }
  };

  const content =
    evals.length > 0 ? (
      <>
        {evals.map(function(evlId) {
          return <EvalListItem evalID={evlId} key={evlId} />;
        })}
      </>
    ) : (
      <Typography className={classes.noEvalsMessage}>
        {/* <ListItemText inset primary="No evaluations yet" /> */}
        No evaluations yet
      </Typography>
    );

  const toolbar = (
    <div className={classes.toolbar}>
      <div className={classes.buttonGroupContainer}>
        {/* <FilterList style={{marginRight: "4px"}} /> */}
        <ToggleButtonGroup
          aria-label="full-width contained primary button group"
          id="mode"
          value={draftMode}
          onChange={handleListMode}
          exclusive
        >
          <ToggleButton value={true} className={classes.modeButton} classes={{ root: classes.modeButtonRoot }}>
            Drafts
          </ToggleButton>
          <ToggleButton value={false} className={classes.modeButton} classes={{ root: classes.modeButtonRoot }}>
            Complete
          </ToggleButton>
        </ToggleButtonGroup>
      </div>

      {draftMode && <NewEvalButton />}
    </div>
  );

  return (
    <div className={classes.root}>
      {toolbar}
      <Divider />

    <div style={{
      flex: 1,
      display: 'flex',
      overflowY: 'scroll',
    }}>

    <Container maxWidth="md" className={classes.content}>
      <Paper elevation={1} className={classes.list}>
        <List>{(fetchingEvals || loadingEvals) ? <LoadingIndicator>Loading Evals</LoadingIndicator> : content}</List>
        <TablePagination
          className={classes.pagination}
          count={totalEvals}
          component="div"
          rowsPerPage={limit}
          page={offset === 0 ? 0 : offset / limit}
          onChangePage={(event, newPage) => dispatch(loadEvaluations(draftMode, limit, newPage * limit))}
          onChangeRowsPerPage={event => dispatch(loadEvaluations(draftMode, parseInt(event!.target.value), 0))}
        />
      </Paper>
    </Container>
</div>
    </div>
  );
};

export default EvalList;
